import React, {useState, useEffect, useRef} from "react";
import {
  getAreaDrClinicList,
  getProgramData,
  session_form_post,
} from "../../../../Services/Service";
import {toast} from "react-toastify";
import styles from "./SessionForm.module.css";
import ProgramForm from "./ProgramForm";
import ExcelFileToArrayComponent from "../../../components/ExcelFileToArrayComponent";

const SessionForm = ({session, onSave, onCancel}) => {
  const [formData, setFormData] = useState({
    sessionName: "",
    sessionDetails: "",
    scheduledDateTime: "", // Combined date and time
    sessionType: "group", // Fixed to group
    maxParticipants: "",
    hostMobile: "",
    hostName: "",
    email: "",
    participantMobiles: "",
    programs: [],
    divisions: [],
    clinics: [],
    invitePetients: [],
  });

  const [errors, setErrors] = useState({});
  const [programOptions, setProgramOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [clinicOptions, setClinicOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const openPicker = () => {
    inputRef.current?.showPicker?.(); // Works in some modern browsers
    inputRef.current?.focus(); // Fallback
  };

  const [allProgrames, setAllProgrames] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch programs
        const programResponse = await getProgramData();
        const programs = programResponse.data.data || [];
        setAllProgrames(
          (programResponse.data.programes || []).filter(
            (i) => i.programName != ""
          )
        );
        setProgramOptions(programs);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (session) {
      const scheduleDate = session.rawScheduledDate || new Date();

      // Format datetime-local input value
      const year = scheduleDate.getFullYear();
      const month = String(scheduleDate.getMonth() + 1).padStart(2, "0");
      const day = String(scheduleDate.getDate()).padStart(2, "0");
      const hours = String(scheduleDate.getHours()).padStart(2, "0");
      const minutes = String(scheduleDate.getMinutes()).padStart(2, "0");
      const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;

      setFormData((prev) => ({
        ...prev,
        sessionName: session.sessionName || "",
        sessionDetails: session.sessionDetails || "",
        scheduledDateTime: datetimeLocal,
        sessionType: "group", // Always group
        maxParticipants: session.maxParticipants || "",
        hostMobile: session.hostMobile || "",
        hostName: session.hostName || "",
        email: session.email || "",
        participantMobiles: session.participantMobiles
          ? session.participantMobiles.join(", ")
          : "",
        clinics: session.clinics || prev.clinics,
        programs: session.program || prev.programs,
        divisions: session.division || prev.divisions,
      }));
    }
  }, [session, programOptions, divisionOptions, clinicOptions]);

  const validateSessionName = (name) => {
    // Only allow letters, spaces, and common punctuation (no numbers)
    const regex = /^[a-zA-Z\s\-'.,:;!?()]+$/;
    return regex.test(name.trim());
  };

  const validateMaxParticipants = (value) => {
    // Only allow positive integers
    const regex = /^\d+$/;
    return regex.test(value) && parseInt(value) > 0;
  };

  const validateMobileNumber = (mobile) => {
    // Indian mobile number validation
    const regex = /^[6-9]\d{9}$/;
    return regex.test(mobile.trim());
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  };

  const validateHostName = (name) => {
    // Only letters and spaces for host name
    const regex = /^[a-zA-Z\s]+$/;
    return regex.test(name.trim());
  };

  const handleChange = (e) => {
    const {name, value} = e.target;

    // Special handling for session name - prevent numeric input
    if (name === "sessionName") {
      // Remove any numeric characters as user types
      const filteredValue = value.replace(/[0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));
    }
    // Special handling for max participants - only allow numbers
    else if (name === "maxParticipants") {
      // Only allow numeric characters
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    }
    // Special handling for host mobile - only allow numbers
    else if (name === "hostMobile") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else if (name === "participantMobiles") {
      // Allow numbers, commas, and spaces only
      const filteredValue = value.replace(/[^0-9,\s]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));
    }

    // Special handling for host name - only allow letters and spaces
    else if (name === "hostName") {
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getInputClassName = (fieldName) => {
    let className = "";
    if (errors[fieldName]) {
      className += ` ${styles.error}`;
    }
    return className;
  };

  const validateForm = () => {
    const newErrors = {};

    // Enhanced session name validation
    if (!formData.sessionName.trim()) {
      newErrors.sessionName = "Session name is required";
    } else if (!validateSessionName(formData.sessionName)) {
      newErrors.sessionName =
        "Session name should only contain letters, spaces, and punctuation (no numbers)";
    } else if (formData.sessionName.trim().length < 3) {
      newErrors.sessionName = "Session name must be at least 3 characters long";
    } else if (formData.sessionName.trim().length > 100) {
      newErrors.sessionName = "Session name cannot exceed 100 characters";
    }

    if (!formData.scheduledDateTime) {
      newErrors.scheduledDateTime = "Scheduled date and time is required";
    } else {
      // Check if the scheduled date is in the past
      const scheduledDate = new Date(formData.scheduledDateTime);
      const now = new Date();
      if (scheduledDate <= now) {
        newErrors.scheduledDateTime =
          "Scheduled date and time must be in the future";
      }
    }

    // Validate participant mobiles if provided
    if (!session && formData.participantMobiles.trim()) {
      const mobiles = formData.participantMobiles
        .split(",")
        .map((m) => m.trim())
        .filter((m) => m);
      const invalidMobiles = mobiles.filter(
        (mobile) => !validateMobileNumber(mobile)
      );
      if (invalidMobiles.length > 0) {
        newErrors.participantMobiles = `Invalid mobile numbers: ${invalidMobiles.join(
          ", "
        )}`;
      }
    }

    // Enhanced max participants validation
    if (!formData.maxParticipants) {
      newErrors.maxParticipants = "Maximum participants is required";
    } else if (!validateMaxParticipants(formData.maxParticipants)) {
      newErrors.maxParticipants =
        "Maximum participants must be a positive number only";
    } else {
      const maxPart = parseInt(formData.maxParticipants);
      if (maxPart < 1) {
        newErrors.maxParticipants = "Maximum participants must be at least 1";
      } else if (maxPart > 1000) {
        newErrors.maxParticipants = "Maximum participants cannot exceed 1000";
      }
    }

    if (!session && !formData.hostMobile.trim()) {
      newErrors.hostMobile = "Host mobile number is required";
    } else if (
      !session &&
      formData.hostMobile.trim() &&
      !validateMobileNumber(formData.hostMobile)
    ) {
      newErrors.hostMobile =
        "Please enter a valid 10-digit mobile number starting with 6-9";
    }

    if (!session && !formData.hostName.trim()) {
      newErrors.hostName = "Host name is required";
    } else if (
      !session &&
      formData.hostName.trim() &&
      !validateHostName(formData.hostName)
    ) {
      newErrors.hostName = "Host name should only contain letters and spaces";
    } else if (!session && formData.hostName.trim().length < 2) {
      newErrors.hostName = "Host name must be at least 2 characters long";
    }

    if (!session && !formData.email.trim()) {
      newErrors.email = "Host email is required";
    } else if (
      !session &&
      formData.email.trim() &&
      !validateEmail(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate participant mobiles if provided
    if (!session && formData.participantMobiles.trim()) {
      const mobiles = formData.participantMobiles
        .split(",")
        .map((m) => m.trim())
        .filter((m) => m);
      const invalidMobiles = mobiles.filter(
        (mobile) => !validateMobileNumber(mobile)
      );
      if (invalidMobiles.length > 0) {
        newErrors.participantMobiles = `Invalid mobile numbers: ${invalidMobiles.join(
          ", "
        )}`;
      }
    }

    if (formData.programs.length === 0) {
      newErrors.programs = "At least one program must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Parse the datetime-local input
      // Parse the datetime-local input and keep it in local timezone
      const scheduledDateTime = new Date(formData.scheduledDateTime);
      // Get the local timezone offset and adjust accordingly
      const timezoneOffset = scheduledDateTime.getTimezoneOffset() * 60000; // offset in milliseconds
      const localDateTime = new Date(
        scheduledDateTime.getTime() - timezoneOffset
      );

      // Prepare participant mobiles as array (only for create mode)
      let participantMobArray = [];
      if (!session && formData.participantMobiles) {
        participantMobArray = formData.participantMobiles
          .split(",")
          .map((mobile) => mobile.trim())
          .filter((mobile) => mobile);
      }

      console.log(
        encodeURIComponent(
          Math.floor(localDateTime.getTime() / 1000).toString()
        )
      );

      // Prepare query parameters with proper encoding
      const queryParams = {
        roomName: encodeURIComponent(formData.sessionName),
        description: encodeURIComponent(formData.sessionDetails),
        startTime: encodeURIComponent(
          Math.floor(localDateTime.getTime() / 1000).toString()
        ), // Local timezone epoch
        bannerImg: encodeURIComponent(""),
        thumbnailImg: encodeURIComponent(""),
        roomId: encodeURIComponent(session ? session.id : "0"),
        classType: encodeURIComponent(formData.sessionType),
        maxParticipant: encodeURIComponent(formData.maxParticipants.toString()),
        program: encodeURIComponent(JSON.stringify(formData.programs)),
        division: encodeURIComponent(JSON.stringify(formData.divisions)),
        clinic: encodeURIComponent(JSON.stringify(formData.clinics)),
      };

      // Only add mobile and participantMob for create mode (when session is null)
      if (!session) {
        queryParams.mobile = encodeURIComponent(formData.hostMobile);
        queryParams.hostName = encodeURIComponent(formData.hostName);
        queryParams.email = encodeURIComponent(formData.email);
        queryParams.participantMob = encodeURIComponent(
          JSON.stringify(participantMobArray)
        );
        queryParams.invitePetients = encodeURIComponent(
          JSON.stringify(formData.invitePetients)
        );
      }

      const queryString = Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key]}`)
        .join("&");

      // Call the API
      const response = await session_form_post(queryString);

      if (response.status === 200 || response.status === 201) {
        // Success - prepare data for parent component
        const processedData = {
          ...formData,
          maxParticipants: parseInt(formData.maxParticipants),
          participantMobiles:
            !session && formData.participantMobiles
              ? formData.participantMobiles
                  .split(",")
                  .map((mobile) => mobile.trim())
                  .filter((mobile) => mobile)
              : [],
          scheduledDate: Math.floor(localDateTime.getTime() / 1000),
          rawScheduledDate: localDateTime,
          host: session ? session.host : formData.hostMobile, // Keep existing host in edit mode
        };

        toast.success(
          session
            ? "Session updated successfully!"
            : "Session created successfully!"
        );
        onSave(processedData);
      } else {
        console.error("API Error:", response);
        toast.error(
          session
            ? "Failed to update session. Please try again."
            : "Failed to save session. Please try again."
        );
      }
    } catch (error) {
      console.error("Error saving session:", error);
      toast.error(
        session
          ? "Failed to update session. Please try again."
          : "Failed to save session. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="projects-section">
      <div className={styles.sessionFormContainer}>
        <div className={styles.formHeader}>
          <button
            type="button"
            onClick={onCancel}
            className={styles.backBtnTop}>
            <i className="bi bi-arrow-left"></i>
            <span>Back</span>
          </button>
          <h1>
            {session
              ? `Edit ${
                  session.sessionType === "private" ? "Private" : "Group"
                } Session`
              : "Create New Group Session"}
          </h1>
          <div className={styles.headerSpacer}></div>
        </div>

        {loading ? (
          <div style={{textAlign: "center", padding: "50px"}}>
            <p>Loading...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.sessionForm}>
            {/* Row 1: Session Name and Session Type */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="sessionName">Session Name *</label>
                <input
                  type="text"
                  id="sessionName"
                  name="sessionName"
                  value={formData.sessionName}
                  onChange={handleChange}
                  className={getInputClassName("sessionName")}
                  placeholder="Enter session name (letters only)"
                  maxLength="100"
                />
                {errors.sessionName && (
                  <span className={styles.errorMessage}>
                    {errors.sessionName}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Session Type</label>
                <div className={styles.toggleContainer}>
                  <div
                    className={`${styles.toggleSwitch} ${styles.toggleRight} ${styles.disabled}`}>
                    <div className={styles.toggleSlider}>
                      <span className={styles.toggleOption}>
                        Private Session
                      </span>
                      <span
                        className={`${styles.toggleOption} ${styles.active}`}>
                        Group Session
                      </span>
                    </div>
                  </div>
                  {/* <span className={styles.disabledLabel}>Fixed to Group Session</span> */}
                </div>
              </div>
            </div>

            {/* Row 2: Maximum Participants and Schedule DateTime */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="maxParticipants">Maximum Participants *</label>
                <input
                  type="text"
                  id="maxParticipants"
                  name="maxParticipants"
                  value={formData.maxParticipants}
                  onChange={handleChange}
                  className={errors.maxParticipants ? styles.error : ""}
                  placeholder="e.g. 50"
                  maxLength="4"
                />
                {errors.maxParticipants && (
                  <span className={styles.errorMessage}>
                    {errors.maxParticipants}
                  </span>
                )}
              </div>

              <div onClick={openPicker} className={styles.formGroup}>
                <label htmlFor="scheduledDateTime">
                  Scheduled Date & Time *
                </label>
                <input
                  ref={inputRef}
                  type="datetime-local"
                  id="scheduledDateTime"
                  name="scheduledDateTime"
                  value={formData.scheduledDateTime}
                  onChange={handleChange}
                  className={errors.scheduledDateTime ? styles.error : ""}
                  min={new Date().toISOString().slice(0, 16)}
                />
                {errors.scheduledDateTime && (
                  <span className={styles.errorMessage}>
                    {errors.scheduledDateTime}
                  </span>
                )}
              </div>
            </div>

            {/* Host Details - Only for create mode */}
            {!session && (
              <>
                {/* Row 3: Host Name and Host Mobile */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="hostName">Host Name *</label>
                    <input
                      type="text"
                      id="hostName"
                      name="hostName"
                      value={formData.hostName}
                      onChange={handleChange}
                      className={errors.hostName ? styles.error : ""}
                      placeholder="Enter host name (letters only)"
                      maxLength="50"
                    />
                    {errors.hostName && (
                      <span className={styles.errorMessage}>
                        {errors.hostName}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="hostMobile">Host Mobile Number *</label>
                    <input
                      type="tel"
                      id="hostMobile"
                      name="hostMobile"
                      value={formData.hostMobile}
                      onChange={handleChange}
                      className={errors.hostMobile ? styles.error : ""}
                      placeholder="9876543210"
                      maxLength="10"
                    />
                    {errors.hostMobile && (
                      <span className={styles.errorMessage}>
                        {errors.hostMobile}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 4: Host Email - Full Width */}
                <div className={styles.formRowFullWidth}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">Host Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? styles.error : ""}
                      placeholder="Enter host email"
                    />
                    {errors.email && (
                      <span className={styles.errorMessage}>
                        {errors.email}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 5: Participant Mobile Numbers and Session Details */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="participantMobiles">
                      Participant Mobile Numbers (optional)
                    </label>
                    <textarea
                      id="participantMobiles"
                      name="participantMobiles"
                      value={formData.participantMobiles}
                      onChange={handleChange}
                      placeholder="e.g. 9876543210,9876543211,9876543212"
                      rows="4"
                      className={`${styles.participantTextarea} ${
                        errors.participantMobiles ? styles.error : ""
                      }`}
                    />
                    {errors.participantMobiles && (
                      <span className={styles.errorMessage}>
                        {errors.participantMobiles}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="sessionDetails">
                      Session Details (optional)
                    </label>
                    <textarea
                      id="sessionDetails"
                      name="sessionDetails"
                      value={formData.sessionDetails}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Enter detailed description of the session..."
                    />
                  </div>
                </div>
              </>
            )}

            {/* Session Details Row for Edit Mode - Full Width */}
            {session && (
              <div className={styles.formRowFullWidth}>
                <div className={styles.formGroup}>
                  <label htmlFor="sessionDetails">
                    Session Details (optional)
                  </label>
                  <textarea
                    id="sessionDetails"
                    name="sessionDetails"
                    value={formData.sessionDetails}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Enter detailed description of the session..."
                  />
                </div>
              </div>
            )}

            {/* Program Selection - Updated to remove dependencies */}
            <ProgramForm
              isEdit={session?.id ? true : false}
              styles={styles}
              programes={allProgrames}
              errors={errors}
              editFormData={{
                programs: formData?.programs,
                divisions: formData?.divisions,
                clinics: formData?.clinics,
              }}
              updateFormData={(data) => {
                setFormData((prev) => {
                  return {
                    ...prev,
                    ...data,
                  };
                });
              }}
            />
            {!session && (
              <ExcelFileToArrayComponent
                label="Bulk Invite Participants"
                onSelect={(data) => {
                  let formatNumbers = data.map((item) => {
                    const keys = Object.keys(item);
                    let _data = {};
                    keys.forEach((key, index) => {
                      switch (index) {
                        case 0:
                          _data.name = item[key];
                          break;
                        case 1:
                          _data.mobile = item[key];
                          break;
                        case 3:
                          _data.extention = item[key];
                          break;
                        default:
                          _data[key] = item[key];
                          break;
                      }
                    });
                    return _data;
                  });
                  setFormData((prev) => {
                    return {
                      ...prev,
                      invitePetients: formatNumbers,
                    };
                  });
                }}
              />
            )}

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={onCancel}
                className={styles.cancelBtn}>
                <span>Cancel</span>
              </button>
              <button
                type="submit"
                className={styles.saveBtn}
                disabled={loading}>
                <span>{session ? "Update Session" : "Create Session"}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SessionForm;
